import { useCallback, useEffect, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { createMutationAtom } from 'atoms/create-resource-atom';
import request from 'lib/request';
import { toastAtom } from 'pages/type-curves/atoms';
import {
  tcHighlightedFormationAtom,
  tcSelectedDevAreaAtom,
  tcUnitResolutionRefreshAtom,
} from 'pages/type-curves/type-curve-filters/atoms';
import { emptySurveyRow, type SurveyRow } from 'components/survey-description';
import type { Pieces } from 'components/geometry-editing';
import type { ApiPayloads } from 'types/api-payloads';
import type { CreatedAreaGeometry } from 'types/api-responses';
import { unitPolygonFrom } from './unit-polygon';
import { useAdoptSavedUnit } from './use-adopt-saved-unit';
import { useUnitBuilder } from './context';
import { useSurveyRowEdit } from './use-survey-row-edit';
import {
  AUTO_RESOLVE_DEFAULT,
  autoResolveOverlapsAtom,
  boundaryUserEditedAtom,
  builderUnitNameAtom,
  savedDraftAtom,
  surveyRowsAtom,
  surveyRowsBaselineAtom,
} from './atoms';

type CreateError = ApiPayloads['areaGeometries']['error'];

const createAtom = createMutationAtom<CreatedAreaGeometry, 'areaGeometries'>();

/** Rails renders `unit_actions` rejections keyed by attribute or as a list. */
type ActionFailure = {
  errors?: Record<string, string[]> | { message?: string }[];
  error?: { code?: string; message?: string };
};

// The unique-per-(unit, formation) validation — a duplicate keep. Treated as
// success (the unit is already protected), not an error.
const isAlreadyKept = (body: unknown): boolean => {
  const errors = (body as ActionFailure | undefined)?.errors;
  if (errors && !Array.isArray(errors)) {
    return (errors.base ?? []).some((m) => /already force-kept/i.test(m));
  }
  return false;
};

// First readable message from a `unit_actions` failure body (attribute-keyed
// `{ errors: { base: [...] } }`, list `{ errors: [{ message }] }`, or coded
// `{ error: { message } }`).
const forceKeepMessage = (body: unknown): string | null => {
  const failure = body as ActionFailure | undefined;
  const errors = failure?.errors;
  if (Array.isArray(errors)) {
    return errors.find((e) => e?.message)?.message ?? null;
  }
  if (errors) {
    const keys = ['base', ...Object.keys(errors).filter((k) => k !== 'base')];
    for (const key of keys) {
      const first = errors[key]?.find?.(Boolean);
      if (first) return first;
    }
  }
  return failure?.error?.message ?? null;
};

/**
 * A survey row reduced to the identity the create + diff care about: its
 * reference-location id and its (order-independent) quarter calls.
 *
 * @param {SurveyRow[]} rows - The rows to normalise.
 * @return {string} A stable signature, `undefined` rows dropped.
 */
const normalizeRows = (rows: SurveyRow[]): string =>
  rows
    .filter((row) => row.location)
    .map(
      (row) => `${row.location?.id}:${[...row.quarterCalls].sort().join(',')}`
    )
    .sort()
    .join('|');

/**
 * The most useful line from a create refusal: the `details` list when the server
 * itemised what was wrong, else the first `errors[].message`, else the top-level
 * message. Undefined when the body carried nothing readable.
 *
 * @param {CreateError} [err] - The 422 body.
 * @return {string | undefined} A subtitle for the error toast.
 */
const createErrorSubtitle = (err?: CreateError): string | undefined => {
  const details = err?.error?.details;
  if (details && details.length > 0) return details.join('; ');
  const messages = (err?.errors ?? [])
    .map((entry) => entry.message)
    .filter((message): message is string => Boolean(message));
  if (messages.length > 0) return messages.join('; ');
  return err?.error?.message ?? undefined;
};

export interface SurveyAreaSave {
  rows: SurveyRow[];
  setRows: (rows: SurveyRow[]) => void;
  /** Append an empty row — the panel's "Add survey area" action. */
  addRow: () => void;
  /** Create + publish the unit. In-flight state is on `saving`. */
  save: () => void;
  saving: boolean;
  /** True once the current rows differ from the ones the unit was loaded with. */
  surveysUpdated: boolean;
  /** Unit name shown in the success banner, cleared by the caller. */
  savedBanner: string | null;
  clearBanner: () => void;
  /**
   * Per-row "Custom (use drawn shape)" flags, keyed by row key. Not part of
   * `SurveyRow` because it is builder-specific and the row type is shared.
   */
  customKeys: Record<string, boolean>;
  setCustom: (rowKey: string, custom: boolean) => void;
}

/**
 * The unit's survey description: load, edit, and persist.
 *
 * Save creates (and publishes) the unit via `POST /area_geometries` with
 * `shared: true` + `active: true`, so the create-time reconciler writes the
 * `development_areas` row in the same call — the one path for a new unit or a
 * cloned one. The source is ALWAYS `type: 'drawn'` carrying the single merged
 * polygon the boundary makes (`unitPolygonFrom`), so the unit is stored as the
 * shape on screen; survey rows shape that polygon but no longer decide the
 * provenance, and every unit created here is therefore bespoke.
 *
 * Survey areas are deliberately NOT pre-filled from the scoped unit: the picker
 * starts empty on every unit change, so a filled row always means the user chose
 * it. An existing unit's acreage therefore comes from measuring its boundary, not
 * from a survey preview.
 *
 * @param {number | null} unitId - The scoped unit; null for an unsaved draft.
 * @param {string | null} unitName - The unit name to create under.
 * @param {Pieces} drawnPieces - The freehand boundary, used when no tract is picked.
 * @return {SurveyAreaSave} Rows, the add action, save state, and the dirty flag.
 */
export function useSurveyAreaSave(
  unitId: number | null,
  unitName: string | null,
  drawnPieces: Pieces
): SurveyAreaSave {
  const setToast = useSetAtom(toastAtom);
  const [{ loading: saving }, runCreate] = useAtom(createAtom);
  // "Auto-resolve overlaps" (cloned units): force-keep the new unit through FOUCS
  // on save, for the formation it was created under.
  const { inventoryWorkspaceId } = useUnitBuilder();
  // A successful create makes the draft a real unit: `useAdoptSavedUnit` resolves
  // the created geometry to its development area and re-points the selection at
  // it, which is what gives both the main page and the builder the saved unit's
  // name (and flips every `unitId`-gated control). The refresh bump is separate
  // and is only about the main page's unit list and dev-area tiles.
  const setSavedDraft = useSetAtom(savedDraftAtom);
  const adoptSavedUnit = useAdoptSavedUnit();
  const bumpUnitResolutionRefresh = useSetAtom(tcUnitResolutionRefreshAtom);
  // Cleared on a successful save; the flag itself is the map's freehand override
  // and no longer feeds the payload.
  const setBoundaryUserEdited = useSetAtom(boundaryUserEditedAtom);
  const autoResolve = useAtomValue(autoResolveOverlapsAtom);
  const setAutoResolve = useSetAtom(autoResolveOverlapsAtom);
  // A draft renamed in the header creates under that name; untouched, it keeps
  // the scoped unit's name (or the default for a unit built from scratch).
  const draftName = useAtomValue(builderUnitNameAtom);
  const formationName = useAtomValue(tcHighlightedFormationAtom);
  // Held in an atom so the map pane can derive its quarter grid from the same
  // rows this panel edits.
  const [rows, setRows] = useAtom(surveyRowsAtom);
  const editRows = useSurveyRowEdit();
  const [baseline, setBaseline] = useAtom(surveyRowsBaselineAtom);
  const selectedDevAreaId = useAtomValue(
    tcSelectedDevAreaAtom
  )?.developmentAreaId;

  const [savedBanner, setSavedBanner] = useState<string | null>(null);
  const [customKeys, setCustomKeys] = useState<Record<string, boolean>>({});

  // Survey areas are never pre-filled from the unit and don't carry across a unit
  // change: reset the picker to a single empty row (empty baseline) whenever the
  // selected unit changes from the header. An empty baseline makes any filled row
  // read as an edit, so Save enables as soon as the user picks a survey area.
  useEffect(() => {
    setRows([emptySurveyRow()]);
    setBaseline([]);
  }, [selectedDevAreaId, setRows, setBaseline]);

  const addRow = useCallback(() => {
    editRows((prev) => [...prev, emptySurveyRow()]);
  }, [editRows]);

  const save = useCallback(() => {
    if (saving) return;

    // One polygon, always. `unitPolygonFrom` merges the boundary's pieces first,
    // so touching shapes save as the single shape they are; a boundary that is
    // genuinely disjoint yields no polygon and the panel has already disabled
    // Save (with a warning), so this is the last-resort guard.
    const { polygon } = unitPolygonFrom(drawnPieces);
    if (!polygon) return;

    runCreate({
      url: '/area_geometries',
      type: 'POST',
      data: {
        name: draftName?.trim() || unitName || 'New Unit',
        category: 'Unit',
        // Always `drawn`: the unit is saved as the polygon on screen, whatever it
        // was built from. Survey rows still shape that polygon (they compose into
        // the boundary), they just no longer decide the provenance.
        source: { type: 'drawn', geojson: polygon },
        metadata: { horizontalUnit: true },
        // Publish in the same call — the create-time reconciler writes the
        // development_areas row only when both are true.
        shared: true,
        active: true,
      },
      onSuccess: (created) => {
        setSavedBanner(created?.name ?? unitName ?? 'Unit');
        // The rows just saved are the new baseline, so Save disables until the
        // user edits again.
        setBaseline(rows);
        setBoundaryUserEdited(false);
        setToast({ kind: 'success', title: 'Unit saved' });

        // Shut Save off now, before the id is known: this is the only thing
        // standing between a double-click and a second unit under the same name.
        setSavedDraft(true);
        // Refresh the main page's unit list and dev-area tiles. Nothing below
        // waits on this: the listing is scoped by workspace, formation and map
        // bounds, so it can legitimately not carry the new unit yet.
        bumpUnitResolutionRefresh((prev) => prev + 1);
        setAutoResolve(AUTO_RESOLVE_DEFAULT);

        if (!created) return;
        void (async () => {
          // Adopt the unit that was just created, so the name in the main page's
          // switcher and in the builder's header stop reading "<source> (copy)".
          // This also resolves the unit's real development-area id, which the
          // force-keep below needs — one lookup serves both.
          const unit = await adoptSavedUnit(created);

          // Force-keep the new unit through FOUCS when "Auto-resolve overlaps" is
          // on. Best-effort: a failure doesn't undo the save, a duplicate keep is
          // fine. Skipped when the id could not be resolved — there would be
          // nothing to send.
          if (!unit || !autoResolve || !formationName) return;

          const controller = new AbortController();
          const res = await request.post<unknown, ActionFailure>(
            `/inventory_workspaces/${inventoryWorkspaceId}/unit_actions`,
            {
              unitAction: {
                // The DEVELOPMENT-AREA id. The create response's `id` is the
                // area geometry's, and the endpoint checks this against the
                // workspace's candidate units — so sending that one came back
                // 422 UNIT_NOT_IN_WORKSPACE every time.
                unitId: unit.id,
                action: 'force_keep',
                formationName,
              },
            },
            controller
          );
          if (res.type === 'success') return;
          if (res.type === 'error' && isAlreadyKept(res.data)) return;
          const reason =
            res.type === 'error' ? forceKeepMessage(res.data) : res.message;
          setToast({
            kind: 'error',
            title: 'Unit saved, but could not auto-resolve overlaps',
            ...(reason ? { subtitle: reason } : {}),
          });
        })();
      },
      onError: (err?: CreateError) =>
        setToast({
          kind: 'error',
          title: 'Could not save the unit',
          subtitle: createErrorSubtitle(err),
        }),
    });
  }, [
    saving,
    rows,
    drawnPieces,
    unitName,
    draftName,
    setBoundaryUserEdited,
    setSavedDraft,
    adoptSavedUnit,
    bumpUnitResolutionRefresh,
    runCreate,
    setBaseline,
    setToast,
    autoResolve,
    formationName,
    inventoryWorkspaceId,
    setAutoResolve,
  ]);

  const setCustom = useCallback((rowKey: string, custom: boolean) => {
    setCustomKeys((prev) => ({ ...prev, [rowKey]: custom }));
  }, []);

  return {
    rows,
    setRows,
    addRow,
    save,
    saving,
    surveysUpdated: normalizeRows(rows) !== normalizeRows(baseline),
    savedBanner,
    clearBanner: () => setSavedBanner(null),
    customKeys,
    setCustom,
  };
}
